import {
  Badge,
  Box,
  Flex,
  FormLabel,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { maybeRoundToString } from '@lib/calculations';
import { SnapSuggestionStatus } from '@snap-types/models/common';
import { PriceStrategy } from '@snap-types/models/enums';
import { SnapQuotePrice } from '@snap-types/models/snap-quote-price';
import { Decimal } from '@snap-types/types/decimal';
import { styles } from '@ui/components/TanstackTable';
import { QuoteLineType } from '@ui/features/quote-builder/types/state/quote-line';
import { QuotePriceType } from '@ui/features/quote-builder/types/state/quote-price';
import { currency } from '@ui/util/currency';

export type QuotePriceMetricsType = QuotePriceType | SnapQuotePrice;

type LimitedQuoteLine = Partial<
  Pick<
    QuoteLineType,
    'automatedLeadTime' | 'leadTime' | 'uuid' | 'priceStrategy'
  >
>;

type QuotePriceMetricsDevViewTableProps = {
  // NOTE: AllPricesAccepted, Automatable, and LeadTimeSuggestedStatus are props
  // because the UI uses optimistic mutations, so the values of this data inside
  // the currentQuoteLine prop may be out of date in an edit-quote interface
  allPricesAccepted: boolean;
  automatable: boolean;
  currentQuotePrice: QuotePriceMetricsType;
  currentQuoteLine: LimitedQuoteLine;
  leadTimeSuggestionStatus: SnapSuggestionStatus;
  quotePriceLines: {
    line: number;
    uuid: string;
    quantity: Decimal;
    suggestedPriceStatus: SnapSuggestionStatus;
  }[];
  setQuotePriceUuid: (uuid: string) => void;
};

interface RecordType {
  name: string;
  format: 'number' | 'currency' | 'percent';
  automated: keyof QuotePriceMetricsType;
  calculated: keyof QuotePriceMetricsType | null;
  user: keyof QuotePriceMetricsType | null;
  final: keyof QuotePriceMetricsType;
  appliesToStrategies: PriceStrategy[] | 'all';
}

const METRICS_RECORD_TYPES: Record<string, RecordType> = {
  quantity: {
    name: 'Quantity',
    format: 'number',
    automated: 'automatedQuantity',
    calculated: null,
    user: 'userQuantity',
    final: 'quantity',
    appliesToStrategies: 'all',
  },
  unitPrice: {
    name: 'Unit Price',
    format: 'currency',
    automated: 'automatedUnitPrice',
    calculated: 'calculatedUnitPrice',
    user: 'userUnitPrice',
    final: 'unitPrice',
    appliesToStrategies: 'all',
  },
  grossMargin: {
    name: 'Gross Margin',
    format: 'percent',
    automated: 'automatedGrossMargin',
    calculated: 'calculatedGrossMargin',
    user: 'userGrossMargin',
    final: 'grossMargin',
    appliesToStrategies: [
      PriceStrategy.TargetGrossMargin,
      PriceStrategy.RAFTargetGrossMargin,
      PriceStrategy.CAPPriceSegmentation,
    ],
  },
  contributionMargin: {
    name: 'Contribution Margin',
    format: 'percent',
    automated: 'automatedContributionMargin',
    calculated: 'calculatedContributionMargin',
    user: 'userContributionMargin',
    final: 'contributionMargin',
    appliesToStrategies: [PriceStrategy.TargetContributionMargin],
  },
  baseGrossMargin: {
    name: 'Target Gross Margin',
    format: 'percent',
    automated: 'automatedBaseGrossMargin',
    calculated: 'calculatedBaseGrossMargin',
    user: null,
    final: 'baseGrossMargin',
    appliesToStrategies: [
      PriceStrategy.PriceSegmentation,
      PriceStrategy.CAPPriceSegmentation,
    ],
  },
};

export function QuotePriceMetricsDevViewTable({
  allPricesAccepted,
  automatable,
  currentQuotePrice,
  currentQuoteLine,
  leadTimeSuggestionStatus,
  quotePriceLines,
  setQuotePriceUuid,
}: QuotePriceMetricsDevViewTableProps) {
  return (
    <>
      <TableContainer width="100%" mb={4}>
        <Table width="100%" sx={styles} size="sm">
          <Thead>
            <Tr>
              <Th width="25%">
                Quote Line:{' '}
                {automatable ? (
                  <Badge backgroundColor="darkgreen" color="white">
                    AUTOMATABLE
                  </Badge>
                ) : (
                  <Badge backgroundColor="darkred" color="white">
                    NOT AUTOMATABLE
                  </Badge>
                )}
              </Th>
              <Th width="25%">Automated</Th>
              <Th width="25%">Final</Th>
              <Th width="25%">Accepted</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td>Lead Time</Td>
              <Td>
                <FormattedValue
                  value={currentQuoteLine.automatedLeadTime}
                  format="number"
                />
              </Td>
              <Td>
                <FormattedValue
                  value={currentQuoteLine.leadTime}
                  format="number"
                />
              </Td>
              <Td>
                <FormattedValue
                  value={leadTimeSuggestionStatus}
                  format="number"
                />
              </Td>
            </Tr>
          </Tbody>
        </Table>
        <Flex flexDirection="row" alignItems="center" gap={4} mt={8} mb={0}>
          <Box display="flex" flexDirection="row" alignItems="center">
            <FormLabel htmlFor="quote-price-select">Price Line:</FormLabel>
            <Select
              width="400px"
              onChange={(e) => setQuotePriceUuid(e.target.value)}
            >
              {quotePriceLines.map((quotePriceLine) => (
                <option key={quotePriceLine.uuid} value={quotePriceLine.uuid}>
                  {quotePriceLine.line + 1}: Quantity{' '}
                  {quotePriceLine.quantity.toString()}{' '}
                  {quotePriceLine.suggestedPriceStatus ===
                  SnapSuggestionStatus.REJECTED
                    ? '(Price Not Accepted)'
                    : ''}
                </option>
              ))}
            </Select>
          </Box>
          {!allPricesAccepted && (
            <Badge backgroundColor="darkred" color="white" fontSize="xl">
              NOT ALL PRICES ACCEPTED
            </Badge>
          )}
        </Flex>
        <Table width="100%" sx={styles} size="sm">
          <Thead>
            <Tr>
              <Th width="25%">
                Pricing:{' '}
                {currentQuotePrice.suggestedPriceStatus ===
                SnapSuggestionStatus.ACCEPTED ? (
                  <Badge backgroundColor="darkgreen" color="white">
                    Accepted
                  </Badge>
                ) : currentQuotePrice.suggestedPriceStatus ===
                  SnapSuggestionStatus.REJECTED ? (
                  <Badge backgroundColor="darkred" color="white">
                    Not Accepted
                  </Badge>
                ) : (
                  'Price Strategy Excluded'
                )}
              </Th>
              <Th width="25%">Automated</Th>
              <Th width="25%">Calculated</Th>
              <Th width="25%">Final</Th>
              <Th width="25%">
                User <i>(DEPRECATED)</i>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {Object.entries(METRICS_RECORD_TYPES)
              .filter(
                ([_key, values]) =>
                  values.appliesToStrategies === 'all' ||
                  values.appliesToStrategies.includes(
                    currentQuoteLine.priceStrategy!,
                  ),
              )
              .map(([key, values]) => (
                <Tr key={key}>
                  <Td key={key}>{values.name}</Td>
                  <Td>
                    <FormattedValue
                      value={currentQuotePrice[values.automated]}
                      format={values.format}
                    />
                  </Td>
                  <Td
                    {...(values.calculated
                      ? {}
                      : { backgroundColor: 'mw.lightGrey' })}
                  >
                    <FormattedValue
                      value={
                        values.calculated
                          ? currentQuotePrice[values.calculated]
                          : 'n/a'
                      }
                      format={values.format}
                    />
                  </Td>
                  <Td>
                    <FormattedValue
                      value={currentQuotePrice[values.final]}
                      format={values.format}
                    />
                  </Td>
                  <Td
                    {...(values.user
                      ? {}
                      : { backgroundColor: 'mw.lightGrey' })}
                  >
                    <FormattedValue
                      value={
                        values.user ? currentQuotePrice[values.user] : 'n/a'
                      }
                      format={values.format}
                    />
                  </Td>
                </Tr>
              ))}
          </Tbody>
        </Table>
        <Flex mt={12} flexDirection="column" gap={0}>
          <Text>
            Temporary: Non-applicable fields for this price strategy; remove
            once development complete.
          </Text>
          <Text>
            These <strong>should all be null.</strong>
          </Text>
          <Table width="100%" sx={styles} size="sm">
            <Tbody>
              {Object.entries(METRICS_RECORD_TYPES)
                .filter(
                  ([_key, values]) =>
                    values.appliesToStrategies != 'all' &&
                    !values.appliesToStrategies.includes(
                      currentQuoteLine.priceStrategy!,
                    ),
                )
                .map(([key, values]) => (
                  <Tr key={key}>
                    <Td key={key}>{values.name}</Td>
                    <Td>
                      <FormattedValue
                        value={currentQuotePrice[values.automated]}
                        format={values.format}
                      />
                    </Td>
                    <Td
                      {...(values.calculated
                        ? {}
                        : { backgroundColor: 'mw.lightGrey' })}
                    >
                      <FormattedValue
                        value={
                          values.calculated
                            ? currentQuotePrice[values.calculated]
                            : 'n/a'
                        }
                        format={values.format}
                      />
                    </Td>
                    <Td>
                      <FormattedValue
                        value={currentQuotePrice[values.final]}
                        format={values.format}
                      />
                    </Td>
                    <Td
                      {...(values.user
                        ? {}
                        : { backgroundColor: 'mw.lightGrey' })}
                    >
                      <FormattedValue
                        value={
                          values.user ? currentQuotePrice[values.user] : 'n/a'
                        }
                        format={values.format}
                      />
                    </Td>
                  </Tr>
                ))}
            </Tbody>
          </Table>
        </Flex>
      </TableContainer>
    </>
  );
}

/**
 * Format a value with appropriate supporting characters ($ / %), rounding,
 * and multiplication
 */
function FormattedValue({
  value,
  format,
}: {
  value: string | number | boolean | Decimal | null | undefined;
  format: 'number' | 'currency' | 'percent';
}) {
  if (value === null || value === undefined) {
    return (
      <Badge backgroundColor="gray.500" color="white">
        <i>null</i>
      </Badge>
    );
  } else if (typeof value === 'boolean') {
    return value ? 'True' : 'False';
  }

  if (value === 'n/a') {
    return <i>n/a</i>;
  }

  if (value === 'n/a') {
    return <i>n/a</i>;
  }

  if (format === 'currency') {
    return currency(value);
  }

  if (format === 'percent') {
    const rounded = maybeRoundToString(
      Number(value) * 100,
      {
        min: 2,
        small: 3,
      },
      '',
    );

    return `${rounded} %`;
  }

  return value.toString();
}
